import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';
import './WelcomePage.css';
import { 
    Volume2, VolumeX, LayoutDashboard, Store, 
    ShoppingBag, Sparkles, Mail, MapPin
} from 'lucide-react';

interface Petal {
    id: number;
    left: string;
    size: string;
    animationDuration: string;
    animationDelay: string;
    type: number;
}

const WelcomePage: React.FC = () => {
    const { isAuthenticated, user, accessToken, loading: authLoading } = useAuth();
    const history = useHistory();
    const [weddingData, setWeddingData] = useState<any>(null);
    const [petals, setPetals] = useState<Petal[]>([]);
    const [loading, setLoading] = useState(true);
    const [isMusicPlaying, setIsMusicPlaying] = useState(false);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        // Redirect if not authenticated
        if (!authLoading && !isAuthenticated) {
            history.push('/login');
            return;
        }

        // Direct redirect for agency users - they should land on agencyend immediately
        if (isAuthenticated && user?.role === 'agency') {
            window.location.href = '/agency/';
            return;
        }

        // Generate petals
        const newPetals: Petal[] = Array.from({ length: 15 }).map((_, i) => ({
            id: i,
            left: `${Math.random() * 100}%`,
            size: `${Math.random() * 15 + 10}px`,
            animationDuration: `${Math.random() * 10 + 10}s`,
            animationDelay: `${Math.random() * 10}s`,
            type: Math.floor(Math.random() * 4)
        }));
        setPetals(newPetals);

        // Fetch wedding details for the current user
        const fetchWedding = async () => {
            if (!accessToken) {
                setLoading(false);
                return;
            }

            try {
                const endpoint = `${API_BASE_URL}/api/wedding/current`;
                const response = await fetch(endpoint, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
                const data = await response.json();
                if (response.ok) {
                    setWeddingData(data.wedding);
                }
            } catch (err) {
                console.error('Failed to fetch wedding details');
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated && !authLoading) {
            fetchWedding();
        }
    }, [isAuthenticated, authLoading, history, accessToken]);

    useEffect(() => {
        if (weddingData?.themeSongUrl && audioRef.current) {
            const playAudio = async () => {
                try {
                    await audioRef.current?.play();
                    setIsMusicPlaying(true);
                } catch (err) {
                    console.log("Autoplay blocked, waiting for interaction");
                }
            };
            playAudio();
        }
    }, [weddingData]);

    const toggleMusic = () => {
        if (audioRef.current) {
            if (isMusicPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch(console.error);
            }
            setIsMusicPlaying(!isMusicPlaying);
        }
    };

    const weddingDate = useMemo(() => {
        if (!weddingData) return '...';
        return new Date(weddingData.weddingDate).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }, [weddingData]);

    const daysRemaining = useMemo(() => {
        if (!weddingData) return null;
        const target = new Date(weddingData.weddingDate).getTime();
        const now = new Date().getTime();
        const diff = target - now;
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    }, [weddingData]);

    const dashboardPath = useMemo(() => {
        if (user?.role === 'superadmin') return '/superadmin/dashboard';
        if (user?.role === 'admin') return '/admin/dashboard';
        if (user?.role === 'photographer') return '/photographer/dashboard';
        if (user?.role === 'agency') return '/agency/';
        return '/dashboard';
    }, [user]);

    if (authLoading || (loading && isAuthenticated)) {
        return <div className="loading-state-hub">Preparing your luxury Shadigram Hub...</div>;
    }

    const groomName = weddingData?.groomName || 'Groom';
    const brideName = weddingData?.brideName || 'Bride';
    const venue = weddingData?.venue || 'The Grand Palace';
    const city = weddingData?.city || 'Varanasi';

    return (
        <div className="welcome-hub-container" onClick={() => !isMusicPlaying && toggleMusic()}>
            {weddingData?.themeSongUrl && (
                <audio
                    ref={audioRef}
                    src={weddingData.themeSongUrl}
                    loop
                    onPlay={() => setIsMusicPlaying(true)}
                    onPause={() => setIsMusicPlaying(false)}
                />
            )}

            {/* Floating Music Toggle FAB */}
            <div
                className="hub-music-toggle"
                onClick={(e) => {
                    e.stopPropagation();
                    toggleMusic();
                }}
            >
                {isMusicPlaying ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </div>

            {/* Falling Petals Background Overlay */}
            <div className="petals-overlay">
                {petals.map((petal) => (
                    <div
                        key={petal.id}
                        className={`petal petal-type-${petal.type}`}
                        style={{
                            left: petal.left,
                            width: petal.size,
                            height: petal.size,
                            animationDuration: petal.animationDuration,
                            animationDelay: petal.animationDelay,
                        }}
                    />
                ))}
            </div>

            <div className="hub-grid">
                {/* 1. Header Card */}
                <header className="welcome-header-card glass-panel">
                    <div className="welcome-title-area">
                        <h2>Welcome Back</h2>
                        <h1>Shadigram Hub Workspace</h1>
                    </div>
                    {daysRemaining !== null && (
                        <div className="welcome-countdown-area">
                            <span className="countdown-number">{daysRemaining}</span>
                            <span className="countdown-label">Days to Wedding</span>
                        </div>
                    )}
                </header>

                {/* 2. Left Column: Wedding Details panel */}
                <aside className="wedding-info-panel glass-panel">
                    <span className="wedding-info-title">The Celebration</span>
                    <h1 className="welcome-names">
                        {groomName} <br />
                        <span style={{ fontSize: '1.5rem', color: '#d4af37', fontFamily: 'Inter' }}>&</span> <br />
                        {brideName}
                    </h1>
                    
                    <div className="wedding-meta-item" style={{ marginTop: '1rem' }}>
                        <span className="wedding-meta-label">Wedding Date</span>
                        <span className="wedding-meta-value">{weddingDate}</span>
                    </div>

                    <div className="wedding-meta-item">
                        <span className="wedding-meta-label">Venue Location</span>
                        <span className="wedding-meta-value">
                            <MapPin size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle', color: '#d4af37' }} />
                            {venue}, {city}
                        </span>
                    </div>

                    {user?.role === 'admin' && user?.weddingPlan && (
                        <div className="plan-status-badge">
                            <Sparkles size={12} />
                            <span>{user.weddingPlan} plan</span>
                        </div>
                    )}
                </aside>

                {/* 3. Right Column: Launcher Tiles Grid */}
                <main className="launcher-tiles-container">
                    {/* Tile 1: Planning Dashboard */}
                    <div 
                        className="launcher-tile glass-panel"
                        onClick={() => history.push(dashboardPath)}
                    >
                        <div className="tile-icon">
                            <LayoutDashboard size={24} />
                        </div>
                        <div className="tile-text-container">
                            <h3 className="tile-title">Planning Dashboard</h3>
                            <p className="tile-desc">Manage guestlists, task checklists, budgets, and track event schedules.</p>
                        </div>
                    </div>

                    {/* Tile 2: Vendor Directory */}
                    <div 
                        className="launcher-tile glass-panel"
                        onClick={() => history.push('/marketplace?tab=stores')}
                    >
                        <div className="tile-icon">
                            <Store size={24} />
                        </div>
                        <div className="tile-text-container">
                            <h3 className="tile-title">Vendor Services</h3>
                            <p className="tile-desc">Book luxury photography, venues, decorators, makeup artists, and caterers.</p>
                        </div>
                    </div>

                    {/* Tile 3: Products Shop */}
                    <div 
                        className="launcher-tile glass-panel"
                        onClick={() => history.push('/marketplace?tab=products')}
                    >
                        <div className="tile-icon">
                            <ShoppingBag size={24} />
                        </div>
                        <div className="tile-text-container">
                            <h3 className="tile-title">Bespoke Shop</h3>
                            <p className="tile-desc">Buy premium custom wedding cards, stationary, guest favors, and return gifts.</p>
                        </div>
                    </div>

                    {/* Tile 4: Dress & Jewelry Rentals */}
                    <div 
                        className="launcher-tile glass-panel"
                        onClick={() => history.push('/marketplace?tab=rentals')}
                    >
                        <div className="tile-icon">
                            <Sparkles size={24} />
                        </div>
                        <div className="tile-text-container">
                            <h3 className="tile-title">Luxury Rentals</h3>
                            <p className="tile-desc">Rent designer bridal lehengas, groom sherwanis, and fine diamond/gold jewelry.</p>
                        </div>
                    </div>

                    {/* Tile 5: Invitation Studio (Full Width Row) */}
                    <div 
                        className="launcher-tile glass-panel studio-tile"
                        onClick={() => history.push('/invite/dashboard')}
                    >
                        <div className="tile-icon">
                            <Mail size={24} />
                        </div>
                        <div className="tile-text-container">
                            <h3 className="tile-title">E-Invitations</h3>
                            <p className="tile-desc">Send personalized invites over WhatsApp, SMS, interactive IVR calls, or Telegram bots with RSVP tracking.</p>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default WelcomePage;
